import { Discount, FiscalDocumentClass, FiscalDocumentType } from "../../modules/fiscal-documents"
import { Branch } from "../../modules/manager"
import { IdentifierType, useSystemStateHooks } from "../../modules/system"
import { Address } from "../../features/address/types"
import { delay } from "../../utils/codeUtils"
import { serverDomain } from "../constants"
import { Headers, ServerConfig } from "./config"
import { FiscalDocumentEmitBody, FiscalDocumentEmitResponseBody, FiscalDocumentPreviewBody, FiscalDocumentPreviewBodyResponse, HoardPriceCorrectionResponseBody, ManualCreditNoteBody, ManualCreditNoteResponseBody, PreviewBodyItem, ServerError, TaxpayerDataBody } from "./types"
import { VatCondition } from "../../modules/administration"
import { toList } from "../../utils/listUtils"
import { TableName } from "../../tables"

export const useServer = () => {
    const { companyId, userId } = useSystemStateHooks()
    const { useLocalPort, localPortDomain } = ServerConfig
    const domain = useLocalPort ? localPortDomain : serverDomain

    const fetchUrl = async <T>(
        url: string,
        init: RequestInit,
        maxRetries = 5
    ): Promise<T> => {
        let lastError: Error | null = null
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                const response = await fetch(url, init)
                
                if (response.status === 502) {
                    lastError = new Error(`Attempt ${attempt} failed with status 502`)
                    if (attempt < maxRetries) {
                        await delay(1)
                        continue
                    }
                } else {
                    const body = await response.json()
                    if (!response.ok) {
                        lastError = new ServerError(
                            response.status,
                            body.error?.code || 'UNKNOWN_ERROR',
                            body.error?.message || 'An unknown error occurred'
                        )
                        attempt = maxRetries
                        continue
                    }
                    return body.data as T
                }
                
                throw new Error(`Request failed with status ${response.status}`);
            } catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error))
                if (attempt < maxRetries) {
                    await delay(1)
                    continue
                } else {
                    throw lastError
                }
            }
        }
        
        throw lastError
    }

    // An accreditation adds money to a cash fund and isn't idempotent: if the response is lost (e.g. a proxy error
    // without CORS headers) the server may have processed it, so retrying could credit the same payment twice
    const accreditMaxRetries = 1

    const getAllBranches = async (
        companyIdParam = companyId,
        userIdParam = userId
    ) => {
        const body = { userId: userIdParam }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body),
        }
        return fetchUrl(`${domain}/${companyIdParam}/branches`, init) as Promise<Branch[]>
    }
    
    const scheduleAccreditSalePayment = async (
        saleId: string,
        paymentTypeId: string,
        destinationId: string,
        saleAmount: number,
        paymentDate?: string
    ) => {
        const body = { userId, paymentTypeId, destinationId, saleAmount, paymentDate }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body),
        }
        return fetchUrl(`${domain}/${companyId}/sales/${saleId}/schedule-accredit-payment`, init)
    }

    const scheduleAccreditHoardPayment = async (
        hoardId: string,
        paymentTypeId: string,
        destinationId: string,
        hoardAmount: number,
        paymentDate?: string
    ) => {
        const body = { userId, paymentTypeId, destinationId, hoardAmount, paymentDate }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body),
        }
        return fetchUrl(`${domain}/${companyId}/hoards/${hoardId}/schedule-accredit-payment`, init)
    }

    const scheduleAccreditCreditPayment = async (
        creditPaymentId: string,
        paymentTypeId: string,
        destinationId: string,
        paymentAmount: number,
        paymentDate?: string
    ) => {
        const body = { userId, paymentTypeId, destinationId, paymentAmount, paymentDate }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body),
        }
        return fetchUrl(`${domain}/${companyId}/creditPayments/${creditPaymentId}/schedule-accredit-payment`, init)
    }

    const accreditSalePayment = async (
        saleId: string,
        paymentTypeId: string,
        destinationId: string,
        saleAmount: number,
        paymentDate?: string
    ) => {
        const body = { userId, paymentTypeId, destinationId, saleAmount, paymentDate }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body),
        }
        return fetchUrl(`${domain}/${companyId}/sales/${saleId}/accredit-payment`, init, accreditMaxRetries)
    }
    
    const accreditHoardPayment = async (
        hoardId: string,
        paymentTypeId: string,
        destinationId: string,
        hoardAmount: number,
        paymentDate?: string
    ) => {
        const body = { userId, paymentTypeId, destinationId, hoardAmount, paymentDate }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body),
        }
        return fetchUrl(`${domain}/${companyId}/hoards/${hoardId}/accredit-payment`, init, accreditMaxRetries)
    }

    const accreditCreditPayment = async (
        creditPaymentId: string,
        paymentTypeId: string,
        destinationId: string,
        paymentAmount: number,
        paymentDate?: string
    ) => {
        const body = { userId, paymentTypeId, destinationId, paymentAmount, paymentDate }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body),
        }
        return fetchUrl(`${domain}/${companyId}/creditPayments/${creditPaymentId}/accredit-payment`, init, accreditMaxRetries)
    }

    const getTaxPayerData = async (taxId: string, companyTaxId?: number): Promise<TaxpayerDataBody> => {
        const init = {
            method: "GET",
            headers: Headers
        }

        // El CUIT emisor autentica contra Facturante: sin esto el server elige el primero con punto de venta.
        const query = companyTaxId !== undefined ? `?companyTaxId=${companyTaxId}` : ''
        return await fetchUrl<TaxpayerDataBody>(`${domain}/${companyId}/taxpayer/${taxId}${query}`, init)
    }

    const invoiceSaleOperationPreview = async (
        companyTaxId: number,
        table: TableName,
        operationId: string,
        documentType: FiscalDocumentType,
        documentClass: FiscalDocumentClass,
        saleProvince: string,
        customerIdentifierType: IdentifierType,
        customerIdentifier: number,
        items: PreviewBodyItem[],
        customerName: string,
        customerVatCondition: VatCondition,
        customerGrossIncomeProvinces?: string[],
        customerEmail?: string,
        customerAddress?: Address,
        fiscalDocumentId?: string,
        discounts?: Discount[]
    ): Promise<FiscalDocumentPreviewBodyResponse> => {
        const body: FiscalDocumentPreviewBody = {
            userId: userId!,
            ...(fiscalDocumentId && { fiscalDocumentId }),
            fiscalData: {
                documentType,
                documentClass,
                companyTaxId,
                saleProvince
            },
            customer: {
                identifierType: customerIdentifierType,
                identifier: customerIdentifier,
                name: customerName,
                vatCondition: customerVatCondition,
                emails: toList(customerEmail),
                grossIncomeProvinces: customerGrossIncomeProvinces,
                address: customerAddress
            },
            items,
            ...(discounts?.length ? { discounts } : {})
        }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body)
        }

        const tablePath = table === TableName.SALE_ORDERS ? 'sales' : 'hoards'
        return await fetchUrl<FiscalDocumentPreviewBodyResponse>(`${domain}/${companyId}/${tablePath}/${operationId}/fiscal-document-preview`, init)
    }

    const emitFiscalDocument = async (
        fiscalDocumentId: string,
        params?: Omit<FiscalDocumentEmitBody, 'userId'>
    ): Promise<FiscalDocumentEmitResponseBody> => {
        const body: FiscalDocumentEmitBody = {
            userId: userId!,
            ...params
        }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body)
        }

        return await fetchUrl<FiscalDocumentEmitResponseBody>(`${domain}/${companyId}/fiscal-documents/${fiscalDocumentId}/emit`, init)
    }

    const generateCreditNote = async (adjustmentNoteId: string): Promise<FiscalDocumentEmitResponseBody> => {
        const body = {
            adjustmentNoteId,
            userId
        }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body)
        }

        return await fetchUrl<FiscalDocumentEmitResponseBody>(`${domain}/${companyId}/fiscal-documents/credit-note`, init)
    }

    // Without retries: if the response is lost the note may have been created, and a retry would create it twice
    const createManualCreditNote = async (
        table: TableName,
        operationId: string,
        params: Omit<ManualCreditNoteBody, 'userId'>
    ): Promise<ManualCreditNoteResponseBody> => {
        const body: ManualCreditNoteBody = {
            userId: userId!,
            ...params
        }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body)
        }

        const tablePath = table === TableName.SALE_ORDERS ? 'sales' : 'hoards'
        return await fetchUrl<ManualCreditNoteResponseBody>(`${domain}/${companyId}/${tablePath}/${operationId}/manual-credit-notes`, init, 1)
    }

    // Without retries, like the manual credit note
    const correctHoardPrice = async (
        hoardId: string,
        productId: string,
        newPrice: number
    ): Promise<HoardPriceCorrectionResponseBody> => {
        const body = {
            userId,
            productId,
            newPrice
        }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body)
        }

        return await fetchUrl<HoardPriceCorrectionResponseBody>(`${domain}/${companyId}/hoards/${hoardId}/price-corrections`, init, 1)
    }

    const refreshFiscalDocumentStatus = async (fiscalDocumentId: string): Promise<FiscalDocumentEmitResponseBody> => {
        const body = {
            userId
        }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body)
        }

        return await fetchUrl<FiscalDocumentEmitResponseBody>(`${domain}/${companyId}/fiscal-documents/${fiscalDocumentId}/refresh-status`, init)
    }

    const annulFiscalDocument = async (fiscalDocumentId: string): Promise<FiscalDocumentEmitResponseBody> => {
        const body = {
            userId
        }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body)
        }

        // Sin reintentos: es una acción con efecto fiscal.
        return await fetchUrl<FiscalDocumentEmitResponseBody>(`${domain}/${companyId}/fiscal-documents/${fiscalDocumentId}/annul`, init, 1)
    }

    const downloadFileFromUrl = async (fileUrl: string, fileName: string) => {
        const body = {
            fileUrl,
            fileName
        }
        const init = {
            method: "POST",
            headers: Headers,
            body: JSON.stringify(body)
        }
        const response = await fetch(`${domain}/download-file`, init)
        if (!response.ok) {
            throw new Error(`Error downloading file: ${response.status}`)
        }

        const blob = await response.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);
    }

    return {
        getAllBranches,
        scheduleAccreditSalePayment,
        scheduleAccreditHoardPayment,
        scheduleAccreditCreditPayment,
        accreditSalePayment,
        accreditHoardPayment,
        accreditCreditPayment,
        getTaxPayerData,
        invoiceSaleOperationPreview,
        emitFiscalDocument,
        generateCreditNote,
        createManualCreditNote,
        correctHoardPrice,
        refreshFiscalDocumentStatus,
        annulFiscalDocument,
        downloadFileFromUrl
    }
}
